import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ErroApi } from './api.ts';
import type { Usuario } from './tipos.ts';

type Contexto = {
  usuario: Usuario | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  cadastrar: (nome: string, email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  recarregar: () => Promise<void>;
};

const SessaoContexto = createContext<Contexto | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);

  /**
   * `/usuarios/me` responde mesmo para conta `pendente` — é o que permite
   * mostrar a tela de "aguardando aprovação" em vez de jogar a pessoa de volta
   * ao login achando que a senha está errada.
   */
  const recarregar = useCallback(async () => {
    try {
      const { usuario } = await api<{ usuario: Usuario }>('/api/usuarios/me');
      setUsuario(usuario);
    } catch (err) {
      if (err instanceof ErroApi && err.status === 401) setUsuario(null);
      else console.error('Falha ao carregar sessão', err);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const entrar = useCallback(
    async (email: string, senha: string) => {
      await api('/api/auth/sign-in/email', { metodo: 'POST', corpo: { email, password: senha } });
      await recarregar();
    },
    [recarregar],
  );

  const cadastrar = useCallback(
    async (nome: string, email: string, senha: string) => {
      await api('/api/auth/sign-up/email', {
        metodo: 'POST',
        corpo: { name: nome, email, password: senha },
      });
      // O better-auth já deixa a sessão aberta, mas a conta nasce `pendente`:
      // recarregar traz o status e o app mostra a tela de espera.
      await recarregar();
    },
    [recarregar],
  );

  const sair = useCallback(async () => {
    await api('/api/auth/sign-out', { metodo: 'POST', corpo: {} }).catch(() => {});
    setUsuario(null);
  }, []);

  return (
    <SessaoContexto.Provider value={{ usuario, carregando, entrar, cadastrar, sair, recarregar }}>
      {children}
    </SessaoContexto.Provider>
  );
}

export function useSessao(): Contexto {
  const ctx = useContext(SessaoContexto);
  if (!ctx) throw new Error('useSessao precisa estar dentro de <ProvedorSessao>');
  return ctx;
}
